import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { t } from 'i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { useOnResize } from '../helper/hooks/onResize';
import ButtonComponent from './ButtonComponent';
import '../styles/popoverMenu.scss';
// Walk up the DOM and return the rect of the first ancestor that forms a
// containing block for `position: fixed` descendants. The viewport itself is
// the containing block by default, but any positioned ancestor with a
// `transform` / `filter` / `perspective` / `will-change` (transform-related)
// or `contain: paint|layout|strict|content` overrides that. Returns null when
// nothing intervenes — caller should treat null as "use viewport (0, 0)".
var findContainingBlockRect = function (el) {
    var node = el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
        var cs = getComputedStyle(node);
        var createsContainingBlock = cs.transform !== 'none' ||
            cs.perspective !== 'none' ||
            (cs.filter && cs.filter !== 'none') ||
            cs.willChange === 'transform' ||
            cs.willChange === 'perspective' ||
            cs.willChange === 'filter' ||
            cs.contain === 'paint' ||
            cs.contain === 'layout' ||
            cs.contain === 'strict' ||
            cs.contain === 'content';
        if (createsContainingBlock)
            return node.getBoundingClientRect();
        node = node.parentElement;
    }
    return null;
};
var PopoverMenu = function (_a) {
    var setClose = _a.setClose, onClose = _a.onClose, elements = _a.elements;
    var _b = useContext(DataContext), selectedTool = _b.selectedTool, manipulative = _b.manipulative;
    var popupRef = useRef(null);
    var _c = useState('left'), side = _c[0], setSide = _c[1];
    var valueMap = useMemo(function () { return Object.fromEntries(elements.map(function (el) { return [el.id, el.value]; })); }, [elements]);
    // Dynamic positioning: place popup beside the parent button using viewport
    // coordinates (the popup is `position: fixed` so it can escape the drawer's
    // `overflow-x: hidden` clipping context). Also clamp vertically inside the
    // drawer and walk the beak to stay aligned with the button.
    var updatePosition = useCallback(function () {
        var _a, _b;
        var el = popupRef.current;
        if (!el)
            return;
        // The popup's DOM parent is the small inline-wrapper around the button
        // (the same node the popup used to read via `offsetParent` before this
        // switched to `position: fixed`). The wrapper hugs the button, so its
        // rect is effectively the button's rect.
        var buttonEl = el.parentElement;
        if (!buttonEl)
            return;
        var buttonRect = buttonEl.getBoundingClientRect();
        // Reset inline styles so measurement reflects natural popup size.
        el.style.removeProperty('--beak-top');
        var popupRect = el.getBoundingClientRect();
        var popupWidth = popupRect.width;
        var popupHeight = popupRect.height;
        // Resolve `--beak-size` to pixels by reading the computed font-size of
        // the popup. parseFloat on the raw `0.625rem` would silently drop the
        // unit and leave us with 0.625, breaking the gap between popup and beak.
        var beakSize = parseFloat(getComputedStyle(el).getPropertyValue('--beak-size'));
        var remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        var beakPx = isNaN(beakSize) ? 10 : beakSize * remPx;
        // Decide which side the popup opens on. Left is the default; flip to
        // right only when there isn't room to the left of the button.
        var wantLeftLeft = buttonRect.left - beakPx - popupWidth;
        var nextSide = wantLeftLeft < 0 ? 'right' : 'left';
        setSide(nextSide);
        // Target viewport coordinates for the popup.
        var viewportLeft = nextSide === 'left'
            ? buttonRect.left - beakPx - popupWidth
            : buttonRect.right + beakPx;
        // Centre the popup vertically on the button, then clamp inside the drawer.
        var buttonCenterY = buttonRect.top + buttonRect.height / 2;
        var viewportTop = buttonCenterY - popupHeight / 2;
        var container = document.querySelector('.drawer-container');
        var containerRect = container === null || container === void 0 ? void 0 : container.getBoundingClientRect();
        if (containerRect) {
            var margin = 8;
            var minTop = containerRect.top + margin;
            var maxTop = containerRect.bottom - margin - popupHeight;
            if (viewportTop < minTop)
                viewportTop = minTop;
            else if (viewportTop > maxTop)
                viewportTop = maxTop;
        }
        // `position: fixed` is interpreted relative to the nearest *containing
        // block*, which is the viewport only if no positioned ancestor has a
        // `transform`/`filter`/`perspective`/`will-change`. The drawer-container
        // (and potentially others up the tree) carries `transform: translateX(...)`
        // for its open/close animation, so it forms the containing block for our
        // popup. Walk up from the popup until we find that ancestor and subtract
        // its offset to convert viewport coords into container-relative coords.
        var cbRect = findContainingBlockRect(el);
        var cbLeft = (_a = cbRect === null || cbRect === void 0 ? void 0 : cbRect.left) !== null && _a !== void 0 ? _a : 0;
        var cbTop = (_b = cbRect === null || cbRect === void 0 ? void 0 : cbRect.top) !== null && _b !== void 0 ? _b : 0;
        el.style.left = "".concat(viewportLeft - cbLeft, "px");
        el.style.top = "".concat(viewportTop - cbTop, "px");
        // Walk the beak so it stays pointing at the button's centre even after
        // the clamp shifted the popup off-centre.
        var beakPct = Math.max(10, Math.min(90, ((buttonCenterY - viewportTop) / popupHeight) * 100));
        el.style.setProperty('--beak-top', "".concat(beakPct, "%"));
    }, []);
    // Initial measurement runs synchronously before paint so the popup never
    // flashes at the (0, 0) default. Re-runs whenever the elements list
    // changes (different denomination targets → different popup width).
    useLayoutEffect(function () {
        updatePosition();
    }, [updatePosition, elements]);
    useOnResize(updatePosition);
    // Close on outside click
    useEffect(function () {
        var handleClickOutside = function (event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                var target = event.target;
                if (target.closest('.exchangeBtnWrapper'))
                    return;
                setClose(false);
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return function () {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [setClose, onClose]);
    // Compute total cents and current denominations from selected moneyPieces
    var selectedMoneyPieces = selectedTool
        .flatMap(function (sid) {
        return sid.startsWith('manipulativeGroup_')
            ? manipulative.filter(function (m) { return m.data.groupId === sid; })
            : [manipulative.find(function (m) { return m.data.id === sid; })];
    })
        .filter(function (m) { return m && m.data.type === 'moneyPiece'; });
    var totalCents = selectedMoneyPieces.reduce(function (sum, m) {
        var mp = m.data;
        return sum + (valueMap[mp.denomination] || 0);
    }, 0);
    // Count how many of each denomination is currently selected
    var currentDenomCounts = {};
    selectedMoneyPieces.forEach(function (m) {
        var d = m.data.denomination;
        currentDenomCounts[d] = (currentDenomCounts[d] || 0) + 1;
    });
    var currentDenoms = new Set(Object.keys(currentDenomCounts));
    var allSameDenom = currentDenoms.size === 1;
    var handleOptionClick = function (targetId) {
        customEventHandler.dispatchEvent('bottomPanel', {
            panel: 'bottomPanel',
            id: "nmp_exchangeTo_".concat(targetId),
            type: 'exchangeBtn',
        });
        setClose(false);
        onClose();
    };
    return (_jsx("div", { className: "popoverMenuWrapper".concat(side === 'right' ? ' popoverMenuWrapper--right' : ''), ref: popupRef, children: elements.map(function (_a, index) {
            var id = _a.id, text = _a.text, value = _a.value;
            var resultCount = Math.floor(totalCents / value);
            var canAfford = resultCount > 0;
            var isSameDenom = allSameDenom && currentDenoms.has(id);
            // Disable if exchange would produce the same count of this denomination as already selected
            var noChange = canAfford && resultCount === (currentDenomCounts[id] || 0);
            var isDisabled = !canAfford || isSameDenom || noChange;
            var label = t(text);
            return (_jsxs(React.Fragment, { children: [index > 0 && _jsx("div", { className: "popoverMenuDivider" }), isDisabled ? (
                    // Disabled options use a native <button disabled> so browsers
                    // exclude them from Tab order entirely (more reliable than
                    // tabindex=-1 alone). aria-disabled echoes the state for screen
                    // readers, and the `.disabled` class drives the dimmed visual.
                    _jsx("button", { type: "button", disabled: true, "aria-disabled": "true", "aria-label": t('text.exchangeTo', { denom: label }), className: "popoverMenuOption disabled", children: label })) : (_jsx(ButtonComponent, { type: "textType5", text: label, defaultCSS: "popoverMenuOption", normalCSS: "popoverMenuOption", clickHandler: function () { return handleOptionClick(id); }, tabIndex: 0, ariaLabel: t('text.exchangeTo', { denom: label }), avoidDefaultStyle: true }))] }, id));
        }) }));
};
export default PopoverMenu;
